import { BookOpen, ChevronRight } from "lucide-react";
import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import { getContentRequest } from "src/redux/slices/contentSlice";
import { Content } from "src/types/content.types";
import Card from "../Card/Card";
import Shimmer from "src/components/common/Shimmer";
import { EVENT_NAMES, SCREEN_NAMES } from "src/helpers/analytics/events";
import analytics from "src/helpers/analytics";

interface EducationalContentCardProps {
  id: string;
  title: string;
  description: string;
  type: string;
  supportProgramKey: string;
  className?: string;
}

const EducationalContentCard: React.FC<EducationalContentCardProps> = ({
  id,
  title,
  description,
  type,
  supportProgramKey,
  className = "mx-4",
}) => {
  const dispatch = useAppDispatch();
  const { content } = useAppSelector((state) => state.content);
  const params = useParams();
  const navigate = useNavigate();
  const screenName = params["screen-name"];

  useEffect(() => {
    getContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getContent = () => {
    new Promise((resolve, reject) => {
      dispatch(getContentRequest({ resolve, reject }));
    })
      .then((res) => {})
      .catch((err) => {
        console.error("Error fetching educational content", err);
      });
  };

  const navigateToContentListScreen = () => {
    // Event: EDUCATIONAL_CONTENT_CARD_RIGHT_ICON_CLICKED
    analytics.trackEvent(
      EVENT_NAMES.EDUCATIONAL_CONTENT_CARD_RIGHT_ICON_CLICKED,
      {
        screen_name: SCREEN_NAMES.HOME_SCREEN,
        card_title: title,
        support_program_key: supportProgramKey,
      }
    );

    // Navigate to educational content list screen
    navigate(`/app/${supportProgramKey}/learn-content`, {
      state: {
        screenName: screenName,
      },
    });
  };

  const navigateToContentDetailsScreen = (content: Content) => {
    navigate(
      `/app/${supportProgramKey}/learn-content/${content?.content_master_id}`,
      {
        state: {
          content,
        },
      }
    );
  };

  return (
    <div className={className}>
      <Card
        title={title}
        description={description}
        icon={<BookOpen className="w-4 h-4" />}
        rightIcon={<ChevronRight className="w-4 h-4" />}
        rightIconClick={navigateToContentListScreen}
      >
        <div className="flex items-start gap-4 overflow-x-auto">
          {content?.isLoading ? (
            <div className="flex items-start gap-4 overflow-x-auto">
              <Shimmer width="200px" height="163px" />
              <Shimmer width="200px" height="163px" />
            </div>
          ) : content?.data?.length > 0 ? (
            content?.data?.map((content: Content) => (
              <div
                key={content.title}
                className="flex flex-col items-start min-w-[200px] h-full bg-[var(--icon-bg-primary)] border border-[var(--app-primary)] rounded-md"
                onClick={() => navigateToContentDetailsScreen(content)}
              >
                <img
                  src={content?.media[0]?.home_thumbnail_url}
                  alt={content.title}
                  className="w-full h-[80px] rounded-md overflow-hidden object-cover"
                />
                <div className="p-3 flex flex-col gap-2 overflow-hidden">
                  <div className="flex items-center gap-2">
                    {content?.topic_name?.split(",")?.map((tag: string) => (
                      <div
                        key={tag}
                        className="flex-shrink-0 text-[10px] font-medium text-[var(--app-primary)] text-center bg-[var(--icon-bg-primary)] border border-[var(--app-primary)] px-1 py-0 rounded-full"
                      >
                        {tag}
                      </div>
                    ))}
                  </div>
                  {/* truncate */}
                  <div className="text-xs font-medium text-black line-clamp-2 h-8">
                    {content.title}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-sm text-gray-500 font-thin">
              No content available at the moment.
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default EducationalContentCard;
